import { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { isTruncated } from '../../../hooks/useTruncationTooltip';
import { useFormatLocale } from '../../../locale/formatting';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { SliderGripLabel } from '../GripLabel';
import { calculatePreciseValue } from '../SliderFormat';
import { SliderGrip } from './SliderGrip';
import { SliderInput } from './SliderInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SliderValueGrip = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    orientation
  } = props;
  const refInput = useRef(null);
  const refGrip = useRef(null);
  const refGripLabel = useRef(null);
  useEffect(() => {
    if (orientation === 'vertical' && refGripLabel.current) {
      const paddingStart = parseInt(window.getComputedStyle(refGripLabel.current).paddingInlineStart) ?? 0;
      const inputWidth = refInput.current?.getInputWidth?.() ?? 0;
      refGripLabel.current.style.minWidth = `${inputWidth + paddingStart}px`;
    }
  }, [orientation]);
  useImperativeHandle(ref, () => ({
    getGripContainerWidth() {
      return refGrip.current?.getGripContainerElement()?.getBoundingClientRect().width;
    },
    getGripLabelElementWidth() {
      return refGripLabel.current?.getBoundingClientRect().width;
    },
    focus() {
      refGrip.current?.getGripElement()?.focus();
    }
  }));
  const isEnabledSingleScale = props.enableSliderWithSingleScaleValue && props.scale.minValue === props.scale.maxValue;
  const position = isEnabledSingleScale ? 0.5 : props.scale.position(props.value);
  const renderGripInput = props.renderGripInput ?? (p => /*#__PURE__*/_jsx(SliderInput, {
    ...p,
    ref: p.ref
  }));
  const shouldHideInput = props.disabled || props.readOnly || props.hideInput;
  const numberFormat = Intl.NumberFormat(useFormatLocale(), {
    maximumFractionDigits: 20
  });
  const [keyboardTooltip, setKeyboardTooltip] = useState('');
  const keyboardRef = useKeyboardTooltip({
    tooltip: keyboardTooltip
  });
  useEffect(() => {
    const labelWrapper = document.getElementById(props.ariaDescribedBy ?? '');
    const label = labelWrapper ? labelWrapper.getElementsByTagName('label')[0] : null;
    const labelIsTruncated = label ? isTruncated(label) : false;
    if (label && labelIsTruncated) {
      const labelText = label.innerText;
      setKeyboardTooltip(labelText);
    } else {
      setKeyboardTooltip('');
    }
  }, [props.ariaDescribedBy]);
  setRef(keyboardRef, refGrip.current?.getGripElement());
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [props.scale.minValue === props.scale.maxValue && props.enableSliderWithSingleScaleValue && props.isRange ? null : /*#__PURE__*/_jsx(SliderGrip, {
      testId: props.testId,
      ariaLabel: props.ariaLabel,
      ariaLabelledBy: props.ariaLabelledBy,
      ariaDescribedBy: props.ariaDescribedBy,
      disabled: props.disabled,
      readOnly: props.readOnly,
      enableSliderWithSingleScaleValue: props.enableSliderWithSingleScaleValue,
      ref: refGrip,
      value: props.value,
      formattedValue: props.format.format(props.value),
      minValue: props.minValue,
      maxValue: props.maxValue,
      type: props.type,
      scale: props.scale,
      clientBounds: props.clientBounds,
      orientation: props.orientation,
      arrowGrips: props.arrowGrips,
      inclusive: props.inclusive,
      zIndex: props.zIndex,
      sliderHasTicks: props.sliderHasTicks,
      onDragStart: props.onDragStart,
      onChange: props.disabled || props.readOnly ? undefined : ({
        position,
        didDrag
      }) => {
        props.onChange && props.onChange({
          value: props.scale.value(position, didDrag)
        });
      },
      onCommit: props.disabled || props.readOnly ? undefined : ({
        position,
        didDrag
      }) => {
        props.onCommit && props.onCommit({
          value: props.scale.value(position, didDrag)
        });
      },
      onClick: shouldHideInput ? undefined : () => {
        refInput.current && refInput.current.focusInput();
      },
      onKeyAction: props.disabled || props.readOnly ? undefined : ({
        type,
        dir
      }) => {
        let value;
        if (type === 'stepMax') {
          value = dir === 'forward' ? props.maxValue : props.minValue;
        } else {
          let step = 0;
          if (type === 'step') {
            step = props.stepValue;
          } else if (type === 'stepTwo') {
            step = props.stepMediumValue;
          } else if (type === 'stepLarge') {
            step = props.stepLargeValue;
          }
          value = props.value;
          if (typeof step === 'number') {
            value += (dir === 'forward' ? 1 : -1) * step;
            value = calculatePreciseValue(value, props.stepValue, numberFormat, props.disableRoundToStep);
          } else {
            value = step(value, {
              type: dir
            });
          }
          if (value > props.maxValue) {
            value = props.maxValue;
          } else if (value < props.minValue) {
            value = props.minValue;
          }
        }
        props.onChange && props.onChange({
          value
        });
        props.onCommit && props.onCommit({
          value
        });
      }
    }), /*#__PURE__*/_jsx(SliderGripLabel, {
      ref: refGripLabel,
      testId: postfixTestId(props.testId, 'label'),
      disabled: props.disabled,
      className: props.gripLabelClassName,
      position: position,
      orientation: props.orientation,
      label: props.format.format(props.value),
      handleLabelTokens: props.handleLabelTokens,
      hideLabel: props.hideLabel,
      isRange: props.isRange,
      onPress: shouldHideInput ? undefined : () => {
        refInput.current && refInput.current.focusInput();
      },
      children: props.scale.minValue === props.scale.maxValue || shouldHideInput ? null : renderGripInput({
        testId: props.testId,
        ref: refInput,
        orientation: props.orientation,
        position,
        value: props.value,
        format: props.format,
        defaultFormat: props.defaultFormat,
        step: props.stepValue,
        minValue: props.minValue,
        maxValue: props.maxValue,
        onCommit: ({
          value
        }) => {
          if (value < props.minValue) {
            value = props.minValue;
          } else if (value > props.maxValue) {
            value = props.maxValue;
          }
          if (props.onChange && props.value !== value) props.onChange({
            value
          });
          props.onCommit && props.onCommit({
            value
          });
        },
        onCancel: () => {
          // Set focus to the grip above.
          refGrip.current?.focusGrip();
        },
        type: props.type,
        ariaLabel: props.ariaLabel,
        ariaLabelledBy: props.ariaLabelledBy,
        getSliderClientRect: props.getSliderClientRect,
        setSliderGripControlIsVisible: props.setSliderGripControlIsVisible,
        setHasSliderInputErrorOrFocus: props.setHasSliderInputErrorOrFocus
      })
    })]
  });
});
//# sourceMappingURL=SliderValueGrip.js.map